/**
 * Public Account Deletion Page
 * Standalone page (no Admin MainLayout) for users to request deletion of their own account.
 * This page is intended to be linked from the mobile app for App Store compliance.
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import {
  Card,
  CardContent,
  TextField,
  PrimaryButton,
  SecondaryButton,
} from '../../components/ui';
import { AccountDeletionService } from '../../services/account/AccountDeletionService';

const AccountDeletionPage: React.FC = () => {
  const theme = useTheme();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const validateForm = (): boolean => {
    const newErrors: { email?: string; password?: string } = {};

    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!password) {
      newErrors.password = 'Password is required';
    } else if (password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleOpenConfirm = () => {
    setApiError(null);

    if (!validateForm()) {
      return;
    }

    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    setIsSubmitting(true);
    setApiError(null);

    try {
      await AccountDeletionService.deleteAccount({
        email: email.trim(),
        password,
      });

      setConfirmOpen(false);
      setEmail('');
      setPassword('');
      navigate('/account/delete/success', { replace: true });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to delete account. Please try again later.';
      setApiError(message);
      setConfirmOpen(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.palette.background.default,
        padding: theme.spacing(3),
      }}
    >
      <Card
        sx={{
          width: '100%',
          maxWidth: 520,
          boxShadow: theme.shapes?.shadow?.lg || theme.shadows[4],
        }}
      >
        <CardContent
          sx={{
            padding: theme.spacing(4),
            '&:last-child': {
              paddingBottom: theme.spacing(4),
            },
          }}
        >
          <Box sx={{ marginBottom: theme.spacing(3) }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: theme.palette.text.primary,
                marginBottom: theme.spacing(1),
              }}
            >
              Delete My MiSpy Account
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
              }}
            >
              To permanently delete your MiSpy account and associated personal data, please enter
              your account email and password. This action cannot be undone.
            </Typography>
          </Box>

          {apiError && (
            <Box sx={{ marginBottom: theme.spacing(2) }}>
              <Alert severity="error">{apiError}</Alert>
            </Box>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2.5) }}>
            <TextField
              label="Email Address"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (errors.email) {
                  setErrors({ ...errors, email: undefined });
                }
              }}
              error={!!errors.email}
              helperText={errors.email}
              fullWidth
              autoComplete="email"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
                '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.error.main,
                  borderWidth: 1.5,
                },
              }}
            />

            <TextField
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (errors.password) {
                  setErrors({ ...errors, password: undefined });
                }
              }}
              error={!!errors.password}
              helperText={errors.password}
              fullWidth
              autoComplete="current-password"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
                '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.error.main,
                  borderWidth: 1.5,
                },
                '& .MuiInputBase-input': {
                  pr: 5,
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box
              sx={{
                padding: theme.spacing(1.5),
                borderRadius: 2,
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.03)'
                    : 'rgba(0, 0, 0, 0.02)',
                border: `1px solid ${
                  theme.customColors?.tableBorder || theme.palette.divider
                }`,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.error.main,
                  fontWeight: 600,
                  display: 'block',
                  marginBottom: theme.spacing(0.5),
                }}
              >
                Important
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  lineHeight: 1.5,
                }}
              >
                Deleting your account is permanent. You will lose access to your MiSpy account,
                missions, and any associated data that is not required to be retained for legal,
                regulatory, or security reasons.
              </Typography>
            </Box>

            <PrimaryButton
              fullWidth
              onClick={handleOpenConfirm}
              disabled={isSubmitting}
              sx={{
                marginTop: theme.spacing(1),
                padding: theme.spacing(1.5),
                backgroundColor: theme.palette.error.main,
                '&:hover': {
                  backgroundColor: theme.palette.error.dark,
                },
              }}
            >
              {isSubmitting ? 'Processing...' : 'Delete My Account'}
            </PrimaryButton>
          </Box>
        </CardContent>
      </Card>

      <Dialog
        open={confirmOpen}
        onClose={() => (isSubmitting ? undefined : setConfirmOpen(false))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Account Deletion</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ marginTop: 1 }}>
            Are you sure you want to permanently delete your MiSpy account associated with{' '}
            <strong>{email.trim() || 'this email'}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <SecondaryButton
            onClick={() => setConfirmOpen(false)}
            disabled={isSubmitting}
          >
            Cancel
          </SecondaryButton>
          <PrimaryButton
            onClick={handleConfirmDelete}
            disabled={isSubmitting}
            sx={{
              backgroundColor: theme.palette.error.main,
              '&:hover': {
                backgroundColor: theme.palette.error.dark,
              },
            }}
          >
            {isSubmitting ? 'Deleting...' : 'Confirm Delete'}
          </PrimaryButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccountDeletionPage;

